/**
 * What is this leg for? — ROADMAP 9.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ASKED AFTER THE MOVE, NEVER BEFORE IT
 *
 * The leg is already in the basket by the time this appears, and closing it
 * without choosing leaves the role unset. That ordering is the whole design:
 * a dialog that BLOCKED the drop would put a decision in the middle of a
 * direct-manipulation gesture, and at 14:50 on expiry day it would collect
 * whatever is nearest the cursor.
 *
 * This project already took that position once, in `checklistDone`:
 * "recorded rather than enforced … a trader who must tick five boxes to take
 * a trade they have already decided on starts ticking without reading." A
 * required label has the same failure, and the cost is worse here, because a
 * role that was clicked reflexively becomes a P&L figure someone quotes.
 *
 * So SKIP is a first-class answer, not a dismissal — and the role stays
 * editable from the leg row afterwards, which is what makes skipping safe.
 *
 * IT ALSO SERVES AS THE CONFIRMATION. A drop released into the gap between
 * two cards does nothing and looks identical to one that worked; this
 * appearing is how the trader knows the leg actually landed, and it names
 * where it went.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useRef } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { LEG_ROLES, LEG_ROLE_LABEL, LEG_ROLE_MEANING, type LegRole } from '@apna/shared'
import { cn } from '@/lib/utils'
import { ROLE_TONE } from './legRoleTone'

export interface LegRoleAsk {
  readonly tradeLegId: string
  /** The contract, so the dialog names what it is asking about. */
  readonly symbol: string
  /** Where it just landed — "Iron Fly", or "Manual". */
  readonly into: string
}

export function LegRoleDialog({
  ask,
  onChoose,
  onSkip,
  saving = false,
}: {
  ask: LegRoleAsk | null
  onChoose: (role: LegRole) => void
  onSkip: () => void
  saving?: boolean
}) {
  const first = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (ask === null) return
    first.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSkip()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [ask, onSkip])

  if (ask === null) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      // Clicking away is SKIP, not cancel — the move already happened and
      // there is nothing here to undo.
      onClick={onSkip}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="What is this leg for?"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[420px] rounded-xl border border-border bg-card p-5 shadow-xl"
        data-testid="leg-role-dialog"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-success" />
          <div className="min-w-0">
            <h2 className="text-[14px] font-semibold">Moved to {ask.into}</h2>
            <p className="num mt-0.5 truncate text-[12px] text-muted-foreground" title={ask.symbol}>
              {ask.symbol}
            </p>
          </div>
        </div>

        <p className="mt-3 text-[12px] text-muted-foreground">
          What is this leg for? It only changes how the journal reads — nothing about the
          position or its exit.
        </p>

        <div className="mt-3 grid gap-1.5">
          {LEG_ROLES.map((role, i) => (
            <button
              key={role}
              ref={i === 0 ? first : undefined}
              type="button"
              disabled={saving}
              onClick={() => onChoose(role)}
              data-testid={`leg-role-${role}`}
              className={cn(
                'rounded-lg border border-border px-3 py-2 text-left transition-colors',
                'hover:border-primary hover:bg-primary/5 disabled:opacity-50',
              )}
            >
              <span className="flex items-center gap-2">
                {/* The same dot the row chip carries, so the colour is
                    learned once rather than per screen. */}
                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', ROLE_TONE[role].dot)} />
                <span className="text-[12.5px] font-semibold">{LEG_ROLE_LABEL[role]}</span>
              </span>
              <span className="mt-0.5 block pl-[14px] text-[11px] leading-snug text-muted-foreground">
                {LEG_ROLE_MEANING[role]}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          {/*
            Stated plainly, because a trader who knows they can decide later
            is a trader who will not click one at random now.
          */}
          <span className="text-[10.5px] text-muted-foreground">
            You can set this later from the leg.
          </span>
          <button
            type="button"
            onClick={onSkip}
            disabled={saving}
            data-testid="leg-role-skip"
            className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  )
}
